import { Component, inject } from '@angular/core';
import { ConfirmService } from '@luzula/luzula-lib';
import { neueZeilenKennung } from 'src/app/data-model';
import { KaFeld } from './ka-felder';
import { KaMaskeComponent } from './ka-maske.component';
import { KaHorizont, neuerKaHorizont } from './ka-modell';
import { KA_HORIZONT_SPALTEN, kaFeldSicher, kaPfadZahl, schreibeKaPfad } from './ka-maske';

/** Die beiden Grenzen aus Feld 26, das im Katalog EIN Feld mit zwei Pfaden ist. */
type Grenze = 'obergrenze' | 'untergrenze';

/** Einmal aus dem Katalog geholt, nicht bei jedem Prüflauf der Vorlage. */
const GRENZEN_FELD = kaFeldSicher('grenzen');

/**
 * Maske "Horizonte" der KA-Aufnahme: die horizontbezogenen Daten I und II als EINE
 * Tabelle, je Horizont eine Zeile.
 *
 * **Das ist der Unterschied zur SEA:** Dort sind Substratfolge und Horizontfolge
 * zwei Tabellen mit eigenen Tiefen. Die Kartieranleitung führt eine Tabelle, in der
 * eine Zeile alles zum Horizont trägt.
 *
 * Gezeigt werden die Spalten der Kurzaufnahme (siehe {@link KA_HORIZONT_SPALTEN});
 * anders als in den drei anderen Masken kostet hier jedes Feld eine SPALTE.
 * Die Reihenfolge kommt aus dem Feldkatalog, also aus dem Bogen: erst die Merkmale
 * beschreiben, dann das Horizontsymbol benennen (Feld 58 steht dort am Ende).
 */
@Component({
	standalone: false,
	selector: 'bp-ka-horizonte',
	templateUrl: './ka-horizonte.component.html',
	styleUrls: [
		'../../detail/hl-area/hl-area.component.css',
		'../../detail/hl-table.component.css',
		'./ka-maske.component.css',
	],
})
export class KaHorizonteComponent extends KaMaskeComponent {
	protected override get maskeId() { return 'horizonte' as const; }

	private confirmService = inject(ConfirmService);

	/** Die Spalten der Tabelle. Lese-Zugriff auf eine feste Liste, siehe `maskeId`. */
	get spalten() {
		return KA_HORIZONT_SPALTEN;
	}

	/** Feld 26: liefert Nummer und Beschriftung für die beiden Grenzspalten. */
	get grenzenFeld(): KaFeld {
		return GRENZEN_FELD;
	}

	get horizonte(): KaHorizont[] {
		return this.aufnahme?.horizonte ?? [];
	}

	/** Beschriftung der Grenzspalten: Feldnummer wie im Bogen, dann welche Grenze. */
	grenzeTitel(grenze: Grenze): string {
		const nr = this.grenzenFeld.nr ? `${this.grenzenFeld.nr} ` : '';
		return grenze === 'obergrenze' ? `${nr}Obergrenze [cm]` : `${nr}Untergrenze [cm]`;
	}

	grenzeName(grenze: Grenze, zeile: number): string {
		return `KA-${grenze}[${zeile}]`;
	}

	grenze(grenze: Grenze, zeile: number): number | undefined {
		return kaPfadZahl(this.data, `ka.horizonte[].${grenze}`, zeile);
	}

	setzeGrenze(grenze: Grenze, zeile: number, wert: unknown): void {
		schreibeKaPfad(this.data, `ka.horizonte[].${grenze}`, wert, zeile);
	}

	/**
	 * Neue Zeile. Ohne Angabe wird angefügt, mit Index davor eingefügt.
	 *
	 * Die Zeile entsteht mit einer frischen Kennung und leer: Alle Felder stehen
	 * ausdrücklich als leerer Text darin (siehe `neuerKaHorizont`), damit sich
	 * dieselbe Zeile in zwei Fassungen wiederfinden lässt.
	 */
	onZeileDazu(i = -1): void {
		if (!this.aufnahme) return;
		const zeile = neuerKaHorizont(neueZeilenKennung());
		if (i < 0) this.aufnahme.horizonte.push(zeile);
		else this.aufnahme.horizonte.splice(i, 0, zeile);
		this.vs.dirty.next(true);
	}

	async onZeileLoeschen(i: number): Promise<void> {
		const zeile = this.horizonte[i];
		if (!this.aufnahme || !zeile) return;
		const symbol = String(zeile.horizontsymbol ?? '').trim();
		const was = symbol !== '' ? `Horizont "${symbol}"` : `Horizont ${i + 1}`;
		if (!(await this.confirmService.confirmDelete(`${was} wirklich löschen?`))) return;

		// Feld-Kommentare dieser Zeile mitnehmen. Ohne Speichern, damit erst der
		// Speichern-Knopf schreibt - wie beim Löschen einer SEA-Zeile.
		this.vs.clearCommentsNoSave(this.grenzeName('obergrenze', i));
		this.vs.clearCommentsNoSave(this.grenzeName('untergrenze', i));
		for (const feld of this.spalten) this.vs.clearCommentsNoSave(this.name(feld, i));

		this.aufnahme.horizonte.splice(i, 1);
		this.vs.dirty.next(true);
	}
}
